import { PROMO_TAG_PRODUCT_AVAILABILITY } from '../constants/promotions';
import type { SanityPromoContentItem, SanityPromoProductAvailability, SanityPromoTag } from '../@types';

export const ALL_PROMO_PRODUCT_AVAILABILITIES: SanityPromoProductAvailability[] = [
  PROMO_TAG_PRODUCT_AVAILABILITY.BOTH,
  PROMO_TAG_PRODUCT_AVAILABILITY.SPORTSBOOK,
  PROMO_TAG_PRODUCT_AVAILABILITY.CASINO,
];

type ProductAccess = {
  hasSportsbookAccess?: boolean;
  hasCasinoAccess?: boolean;
};

type ProductAvailabilityState = ProductAccess & {
  isProductAvailabilityResolved?: boolean;
};

export const getAllowedPromoProductAvailabilities = ({
  hasSportsbookAccess,
  hasCasinoAccess,
}: ProductAccess): SanityPromoProductAvailability[] => {
  if (hasSportsbookAccess && hasCasinoAccess) {
    return ALL_PROMO_PRODUCT_AVAILABILITIES;
  }

  if (hasSportsbookAccess) {
    return [PROMO_TAG_PRODUCT_AVAILABILITY.BOTH, PROMO_TAG_PRODUCT_AVAILABILITY.SPORTSBOOK];
  }

  if (hasCasinoAccess) {
    return [PROMO_TAG_PRODUCT_AVAILABILITY.BOTH, PROMO_TAG_PRODUCT_AVAILABILITY.CASINO];
  }

  return [];
};

export const getPromoProductVisibilityState = ({
  hasSportsbookAccess,
  hasCasinoAccess,
  isProductAvailabilityResolved,
}: ProductAvailabilityState) => {
  const allowedProductAvailabilities = getAllowedPromoProductAvailabilities({
    hasSportsbookAccess,
    hasCasinoAccess,
  });

  return {
    allowedProductAvailabilities,
    isLoading: !isProductAvailabilityResolved,
    queriesEnabled: Boolean(isProductAvailabilityResolved && allowedProductAvailabilities.length > 0),
  };
};

export const getPromoTagProductAvailability = (tag: SanityPromoTag): SanityPromoProductAvailability =>
  tag.productAvailability ?? PROMO_TAG_PRODUCT_AVAILABILITY.BOTH;

export const isPromoTagAllowedForProducts = (
  tag: SanityPromoTag,
  allowedProductAvailabilities: SanityPromoProductAvailability[]
) => allowedProductAvailabilities.includes(getPromoTagProductAvailability(tag));

export const filterPromoTagsByProductAvailability = (
  tags: SanityPromoTag[] | undefined,
  allowedProductAvailabilities: SanityPromoProductAvailability[]
) => (tags ?? []).filter((tag) => isPromoTagAllowedForProducts(tag, allowedProductAvailabilities));

export const filterPromoContentByProductAvailability = <T extends SanityPromoContentItem>(
  promotions: T[] | undefined,
  allowedProductAvailabilities: SanityPromoProductAvailability[]
) =>
  (promotions ?? []).reduce<T[]>((visiblePromotions, promotion) => {
    if (!promotion.tags || promotion.tags.length === 0) {
      return [...visiblePromotions, promotion];
    }

    const visibleTags = filterPromoTagsByProductAvailability(promotion.tags, allowedProductAvailabilities);

    if (visibleTags.length === 0) {
      return visiblePromotions;
    }

    return [...visiblePromotions, { ...promotion, tags: visibleTags }];
  }, []);
